import * as React from 'react';
import { t } from '@reos/i18n';
import { FieldRow } from '@redhand/ui';
import { humanApiError } from '../lib/form-errors.js';
import {
  ACTIVITY_KINDS,
  activityDraftProblem,
  activityKindSpec,
  buildActivityRecord,
  type ActivityDraft,
  type ActivityKind,
} from '../lib/record-activity.js';
import { recordActivity, type ActivityTarget } from '../lib/record-activity-api.js';
import { DateField } from './date-field.js';

/**
 * Krok SEZ1 bod 2 — JEDNO TLACITKO „ZAPSAT" u klienta i u zakazky.
 *
 * Tvar zapisu (nazev, popis, stav, termin) pocita `lib/record-activity.ts`,
 * odeslani dela `lib/record-activity-api.ts`. Tahle komponenta je JEN
 * formular, aby obe obrazovky mely stejnou nabidku typu — nabidka se cte z
 * `ACTIVITY_KINDS`, ne z deviti tlacitek napsanych rucne na dvou mistech.
 *
 * Mobil 390: nabidka je nativni `<select>` a pole jsou pod sebou (grid s
 * jednim sloupcem), takze se formular vejde bez vodorovneho posouvani.
 */

const cs = { locale: 'cs' } as const;

const EMPTY_DRAFT: ActivityDraft = { kind: 'meeting', text: '', dueAt: null, amount: '' };

/** `<input type="date">` da `YYYY-MM-DD`; API chce ISO datum a cas. */
export function dateInputToIso(value: string): string | null {
  if (value === '') return null;
  const parsed = new Date(`${value}T12:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export function RecordActivityButton({
  target,
  onRecorded,
}: {
  readonly target: ActivityTarget;
  readonly onRecorded?: (() => void) | undefined;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<ActivityDraft>(EMPTY_DRAFT);
  const [dueInput, setDueInput] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState<string | null>(null);

  const spec = activityKindSpec(draft.kind);
  const problem = activityDraftProblem(draft);

  const reset = (): void => {
    setDraft(EMPTY_DRAFT);
    setDueInput('');
    setError(null);
  };

  const pickKind = (kind: ActivityKind): void => {
    setDraft((prev) => ({ ...prev, kind }));
    setError(null);
  };

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (problem !== null) return;
    setSaving(true);
    setError(null);
    try {
      const record = buildActivityRecord({ ...draft, dueAt: dateInputToIso(dueInput) });
      await recordActivity(target, record);
      setSaved(record.historyText);
      setOpen(false);
      reset();
      onRecorded?.();
    } catch (e) {
      setError(humanApiError(e, 'webAdmin.recordActivity.saveError'));
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <>
        <button
          type="button"
          className="rh-a2-btn"
          onClick={() => {
            setSaved(null);
            setOpen(true);
          }}
          data-testid="zapsat-open"
        >
          {t('webAdmin.recordActivity.openButton', cs)}
        </button>
        {saved !== null ? (
          <p role="status" className="rh-a2-meta" data-testid="zapsat-saved">
            {t('webAdmin.recordActivity.savedPrefix', { ...cs, values: { text: saved } })}
          </p>
        ) : null}
      </>
    );
  }

  return (
    <form onSubmit={(event) => void submit(event)} data-testid="zapsat-form">
      {/*
        Pole skladá `FieldRow` z `@redhand/ui` (popisek NAD prvkem, prvek
        `width: 100%`) — tentyz primitiv, jaky uz ma formular kontaktu
        klienta. Je to podminka MOBILU 390 ze zadani: popisek vedle prvku by
        se s nejdelsi polozkou nabidky („Nekomunikuje / nezvedá") do sirky
        telefonu nevesel.
      */}
      <FieldRow label={t('webAdmin.recordActivity.kindLabel', cs)} htmlFor="zapsat-typ">
        <select
          id="zapsat-typ"
          value={draft.kind}
          onChange={(event) => pickKind(event.target.value as ActivityKind)}
          data-testid="zapsat-typ"
        >
          {ACTIVITY_KINDS.map((item) => (
            <option key={item.kind} value={item.kind}>
              {item.label}
            </option>
          ))}
        </select>
      </FieldRow>

      {spec.asksAmount ? (
        <FieldRow label={t('webAdmin.recordActivity.amountLabel', cs)} htmlFor="zapsat-cena" required>
          {/*
            Cena je TEXT, ne cislo: zapis ji jen cituje do historie (penize se
            v REOS necitaji z `number` — CLAUDE.md §3). `inputMode` presto da
            na mobilu ciselnou klavesnici.
          */}
          <input
            id="zapsat-cena"
            type="text"
            inputMode="numeric"
            value={draft.amount}
            placeholder={spec.placeholder}
            onChange={(event) => setDraft((prev) => ({ ...prev, amount: event.target.value }))}
            data-testid="zapsat-cena"
          />
        </FieldRow>
      ) : (
        <FieldRow
          label={t('webAdmin.recordActivity.textLabel', cs)}
          htmlFor="zapsat-text"
          required={spec.requiresText}
        >
          <textarea
            id="zapsat-text"
            rows={3}
            value={draft.text}
            placeholder={spec.placeholder}
            onChange={(event) => setDraft((prev) => ({ ...prev, text: event.target.value }))}
            data-testid="zapsat-text"
          />
        </FieldRow>
      )}

      {spec.plans ? (
        <FieldRow label={t('webAdmin.recordActivity.dueLabel', cs)}>
          {/*
            Datum se v adminu zadava TEXTEM v ceskem zapisu — nativni
            `<input type="date">` kresli Chromium podle jazyka OS
            (`mm/dd/yyyy`) a hlida to `test/datum-cs-hlidac.spec.ts`.
            Hodnota navenek zustava strojova `YYYY-MM-DD`.
          */}
          <DateField
            value={dueInput}
            onValueChange={setDueInput}
            ariaLabel={t('webAdmin.recordActivity.dueLabel', cs)}
            testId="zapsat-termin"
          />
        </FieldRow>
      ) : null}

      <p className="rh-a2-meta" data-testid="zapsat-napoveda">
        {spec.plans
          ? t('webAdmin.recordActivity.hintPlan', cs)
          : target.kind === 'case'
            ? t('webAdmin.recordActivity.hintCaseHistory', cs)
            : t('webAdmin.recordActivity.hintClientHistory', cs)}
      </p>

      {error !== null ? (
        <p role="alert" className="rh-a2-error" data-testid="zapsat-error">
          {error}
        </p>
      ) : null}

      <div className="rh-a2-actions">
        <button
          type="button"
          className="rh-a2-btn"
          onClick={() => {
            setOpen(false);
            reset();
          }}
          data-testid="zapsat-zrusit"
        >
          {t('webAdmin.recordActivity.cancelButton', cs)}
        </button>
        <button
          type="submit"
          className="rh-a2-btn rh-a2-btn--primary"
          disabled={saving || problem !== null}
          data-testid="zapsat-ulozit"
        >
          {saving ? t('webAdmin.recordActivity.savingButton', cs) : t('webAdmin.recordActivity.saveButton', cs)}
        </button>
      </div>
    </form>
  );
}
